import { LocalizedText, LocalizedElement, sourceAttribute, useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useOptionalAuth } from "../context/AuthContext";
import { buildAuthHeaders } from "../forum/authHeaders";
import {
  PremiumActionConfirmationProvider,
  premiumActionConfiguration,
  usePremiumActionConfirmation,
} from "./PremiumActionConfirmation";
import "./MemberRelationshipAction.css";

// A few isolated page tests replace AuthContext with a deliberately minimal
// module exposing only useAuth. The real application always provides the
// optional hook; the fallback keeps those previews inert instead of crashing.
const optionalAuthHook = typeof useOptionalAuth === "function" ? useOptionalAuth : () => null;

const EMPTY_SNAPSHOT = Object.freeze({
  viewer: "",
  loaded: false,
  loading: false,
  relationships: Object.freeze({}),
});

let snapshot = EMPTY_SNAPSHOT;
let loadingPromise = null;
const listeners = new Set();

function normalizeUsername(value) {
  return String(value || "").trim().toLocaleLowerCase("fr-FR");
}

function emit(nextSnapshot) {
  snapshot = nextSnapshot;
  listeners.forEach((listener) => listener(snapshot));
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function relationship(status, requestId = null) {
  return { status, requestId };
}

async function loadRelationships(viewer, force = false) {
  const viewerKey = normalizeUsername(viewer);
  if (!viewerKey) return EMPTY_SNAPSHOT;

  if (snapshot.viewer !== viewerKey) {
    loadingPromise = null;
    emit({ ...EMPTY_SNAPSHOT, viewer: viewerKey });
  }
  if (!force && snapshot.loaded) return snapshot;
  if (!force && loadingPromise) return loadingPromise;

  emit({ ...snapshot, loading: true });
  loadingPromise = Promise.all([
    fetch("/api/auth/friends/", { headers: buildAuthHeaders() }),
    fetch("/api/auth/friend-requests/", { headers: buildAuthHeaders() }),
  ])
    .then(async ([friendsResponse, requestsResponse]) => {
      if (!friendsResponse.ok || !requestsResponse.ok) {
        throw new Error("Relations indisponibles.");
      }
      const [friends, requests] = await Promise.all([
        friendsResponse.json(),
        requestsResponse.json(),
      ]);
      const relationships = {};
      (Array.isArray(friends) ? friends : []).forEach((friend) => {
        const key = normalizeUsername(friend?.username);
        if (key) relationships[key] = relationship("friend");
      });
      (Array.isArray(requests?.incoming) ? requests.incoming : []).forEach((request) => {
        const key = normalizeUsername(request?.from_username);
        if (key && relationships[key]?.status !== "friend") {
          relationships[key] = relationship("incoming_pending", request?.id || null);
        }
      });
      (Array.isArray(requests?.outgoing) ? requests.outgoing : []).forEach((request) => {
        const key = normalizeUsername(request?.to_username);
        if (key && relationships[key]?.status !== "friend") {
          relationships[key] = relationship("outgoing_pending", request?.id || null);
        }
      });
      const next = {
        viewer: viewerKey,
        loaded: true,
        loading: false,
        relationships,
      };
      emit(next);
      return next;
    })
    .catch(() => {
      const next = { ...snapshot, loaded: true, loading: false };
      emit(next);
      return next;
    })
    .finally(() => {
      loadingPromise = null;
    });

  return loadingPromise;
}

function setRelationship(username, nextRelationship) {
  const key = normalizeUsername(username);
  if (!key) return;
  emit({
    ...snapshot,
    loaded: true,
    relationships: {
      ...snapshot.relationships,
      [key]: nextRelationship,
    },
  });
}

function broadcastRelationship(username, status, conversationId = null) {
  window.dispatchEvent(
    new CustomEvent("eternua:friendships-updated", {
      detail: { username, status, conversationId },
    })
  );
  if (status === "friend") {
    window.dispatchEvent(new CustomEvent("eternua:chat-updated"));
  }
}

function MemberRelationshipActionContent({
  username,
  friendSource = "general",
  friendContext = null,
  className = "",
}) {
  useSourceLanguage();
  const auth = optionalAuthHook();
  const [storeSnapshot, setStoreSnapshot] = useState(snapshot);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState("");
  const requestConfirmation = usePremiumActionConfirmation();
  const navigate = useNavigate();
  const target = String(username || "").trim();
  const targetKey = normalizeUsername(target);
  const viewer = String(auth?.user?.username || "").trim();
  const isSelf = Boolean(targetKey && targetKey === normalizeUsername(viewer));

  useEffect(() => subscribe(setStoreSnapshot), []);

  useEffect(() => {
    if (!viewer || isSelf) return;
    loadRelationships(viewer);
  }, [isSelf, viewer]);

  const current = useMemo(
    () => storeSnapshot.relationships[targetKey] || relationship("none"),
    [storeSnapshot.relationships, targetKey]
  );

  if (!target || !auth || auth.loading || isSelf) return null;

  const baseClass = `member-relationship-action ${className}`.trim();
  const profilePath = `/profil/${encodeURIComponent(target)}?friend_source=${encodeURIComponent(friendSource)}`;
  const contextualRequest = () => {
    const supplied = friendContext && typeof friendContext === "object" ? friendContext : {};
    const currentUrl = typeof window !== "undefined"
      ? `${window.location.pathname || "/"}${window.location.search || ""}`
      : "";
    return {
      source_id: String(supplied.source_id || supplied.id || "").slice(0, 160),
      title: String(
        supplied.title || (typeof document !== "undefined" ? document.title : "") || ""
      ).slice(0, 180),
      url: String(supplied.url || currentUrl || "").slice(0, 500),
      message: String(supplied.message || "").slice(0, 280),
    };
  };

  if (!auth.user) {
    return (
      <LocalizedElement elementType={"a"} copyAttributes={["aria-label"]}
        className={`${baseClass} member-relationship-action--add`}
        href={`/login?next=${encodeURIComponent(profilePath)}`}
        aria-label={sourceAttribute(`Se connecter pour ajouter @${target} en ami`, "Se connecter pour ajouter @__ET_VALUE_0__ en ami")}
      >
        <span aria-hidden="true">＋</span><LocalizedText source={" Ami"} /></LocalizedElement>
    );
  }

  if (!storeSnapshot.loaded || storeSnapshot.viewer !== normalizeUsername(viewer)) {
    return null;
  }

  const perform = async (url, body) => {
    setBusy(true);
    setLocalError("");
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: buildAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.detail || "Action impossible.");
      return payload;
    } catch (error) {
      const message = error?.message || "Action impossible.";
      setLocalError(message);
      throw new Error(message);
    } finally {
      setBusy(false);
    }
  };

  const confirmRelationship = ({ heading, actionLabel, confirmLabel, consentLabel }, action) => {
    requestConfirmation(premiumActionConfiguration({
      universe: "amis",
      heading,
      title: `@${target}`,
      description: "Cette confirmation protège la mise à jour de votre cercle social Eternua.",
      actionLabel,
      date: "Mise à jour immédiate",
      location: `Contexte · ${friendSource || "communauté"}`,
      organizer: target,
      confirmLabel,
      consentLabel,
      consentDetail: "La relation ne sera modifiée qu’après votre accord explicite.",
    }), action);
  };

  if (current.status === "friend") {
    return (
      <LocalizedElement elementType={"button"} copyAttributes={["aria-label"]}
        type="button"
        className={`${baseClass} member-relationship-action--message`}
        onClick={() => {
          window.dispatchEvent(
            new CustomEvent("eternua:open-messenger", { detail: { username: target } })
          );
        }}
        aria-label={sourceAttribute(`Écrire à @${target} dans la messagerie Eternua`, "Écrire à @__ET_VALUE_0__ dans la messagerie Eternua")}
      >
        <span aria-hidden="true">✉</span><LocalizedText source={" Message"} /></LocalizedElement>
    );
  }

  if (current.status === "outgoing_pending") {
    return (
      <LocalizedElement elementType={"span"} copyAttributes={["title"]} className={`${baseClass} member-relationship-action--pending`} title={sourceAttribute("Demande d’ami envoyée")}><LocalizedText source={"✓ Demande envoyée"} /></LocalizedElement>
    );
  }

  if (current.status === "incoming_pending") {
    return (
      <LocalizedElement elementType={"button"} copyAttributes={["aria-label"]}
        type="button"
        className={`${baseClass} member-relationship-action--accept`}
        disabled={busy}
        onClick={() => confirmRelationship({
          heading: "Confirmer l’ajout à votre cercle",
          actionLabel: "Accepter la demande d’ami",
          confirmLabel: "Accepter en ami",
          consentLabel: `Je confirme l’ajout de @${target} à mon cercle.`,
        }, async () => {
          const payload = await perform(
            `/api/auth/friend-requests/${current.requestId}/respond/`,
            { action: "accept" }
          );
          setRelationship(target, relationship("friend", current.requestId));
          broadcastRelationship(target, "friend", payload?.conversation_id || null);
          navigate(payload?.conversation_url || `/amis/salon/${encodeURIComponent(target)}`);
        })}
        aria-label={sourceAttribute(`Accepter la demande d’ami de @${target}`, "Accepter la demande d’ami de @__ET_VALUE_0__")}
        title={localError || undefined}
      >
        {busy ? <LocalizedText source={"Acceptation…"} /> : <LocalizedText source={"Accepter en ami"} />}
      </LocalizedElement>
    );
  }

  return (
    <LocalizedElement elementType={"button"} copyAttributes={["aria-label","title"]}
      type="button"
      className={`${baseClass} member-relationship-action--add${localError ? " is-error" : ""}`}
      disabled={busy}
      onClick={() => confirmRelationship({
        heading: "Confirmer la demande d’ami",
        actionLabel: "Envoyer une demande d’ami",
        confirmLabel: "Envoyer la demande",
        consentLabel: `Je confirme l’envoi d’une demande d’ami à @${target}.`,
      }, async () => {
        const context = contextualRequest();
        if (context.url === "/") context.url = "";
        const payload = await perform("/api/auth/friend-requests/", {
          username: target,
          source: friendSource,
          ...(Object.values(context).some(Boolean) ? { context } : {}),
        });
        const accepted = payload?.request?.status === "accepted";
        const nextStatus = accepted ? "friend" : "outgoing_pending";
        setRelationship(target, relationship(nextStatus, payload?.request?.id || null));
        broadcastRelationship(target, nextStatus, payload?.conversation_id || null);
        if (accepted) {
          navigate(payload?.conversation_url || `/amis/salon/${encodeURIComponent(target)}`);
        }
      })}
      aria-label={sourceAttribute(`Ajouter @${target} en ami`, "Ajouter @__ET_VALUE_0__ en ami")}
      title={localError || sourceAttribute("Ajouter cette personne à mes amis")}
    >
      <span aria-hidden="true">＋</span> {busy ? <LocalizedText source={"Envoi…"} /> : localError ? <LocalizedText source={"Indisponible"} /> : <LocalizedText source={"Ami"} />}
    </LocalizedElement>
  );
}

export default function MemberRelationshipAction(props) {
  useSourceLanguage();
  return <PremiumActionConfirmationProvider><MemberRelationshipActionContent {...props} /></PremiumActionConfirmationProvider>;
}

export function resetMemberRelationshipsForTests() {
  loadingPromise = null;
  emit(EMPTY_SNAPSHOT);
}
